// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useMemo, useState } from 'react';
import {
  Segment,
  Header,
  Form,
  Button,
  Icon,
  Label,
  Message,
  Modal,
  Divider,
  List,
  Popup,
} from 'semantic-ui-react';
import {
  brokerageIntegrationProviderApi,
  BrokerageIntegrationProvider,
  ConfigureProviderRequest,
  ValidateProviderResponse,
} from '../../api/brokerageIntegrationProviderApi';
import { Brokerage } from '../../api/brokerageApi';
import CapabilityToggleRow from './CapabilityToggleRow';
import RedirectUriHelper from './RedirectUriHelper';

interface Props {
  brokerageId: string;
  brokerage: Brokerage | null;
  existing: BrokerageIntegrationProvider | null;
  onUpdated: (provider: BrokerageIntegrationProvider) => void;
  onDeleted: () => void;
}

/**
 * Microsoft provider credentials card + capability toggles. Phase 1
 * functional toggle is SSO only; Calendar/Email rows render disabled with
 * an explanatory tooltip.
 *
 * "Save" is gated on a successful Validate within the same edit session.
 * The validation result is held in component state, not the server, so
 * the platform admin must Validate after every credential change.
 */
function MicrosoftProviderCard({
  brokerageId,
  brokerage,
  existing,
  onUpdated,
  onDeleted,
}: Props) {
  const [clientId, setClientId] = useState(existing?.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [tenantId, setTenantId] = useState(existing?.tenantId ?? '');
  const [displayName, setDisplayName] = useState(existing?.displayName ?? '');
  const [contactEmail, setContactEmail] = useState(existing?.contactEmail ?? '');
  const [contactName, setContactName] = useState(existing?.contactName ?? '');
  const [showSecret, setShowSecret] = useState(false);

  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validation, setValidation] = useState<ValidateProviderResponse | null>(null);

  const dirty = useMemo(() => {
    return (
      clientId !== (existing?.clientId ?? '') ||
      !!clientSecret ||
      tenantId !== (existing?.tenantId ?? '') ||
      displayName !== (existing?.displayName ?? '') ||
      contactEmail !== (existing?.contactEmail ?? '') ||
      contactName !== (existing?.contactName ?? '')
    );
  }, [clientId, clientSecret, tenantId, displayName, contactEmail, contactName, existing]);

  const canValidate = clientId.trim().length > 0 && clientSecret.trim().length > 0 && tenantId.trim().length > 0;
  // Save is gated on a successful validation within this session; if the form is
  // unchanged from persisted state, Save is unnecessary and disabled.
  const canSave = validation?.valid === true && dirty;

  const request = (): ConfigureProviderRequest => ({
    clientId: clientId.trim(),
    clientSecret: clientSecret,
    tenantId: tenantId.trim() || undefined,
    displayName: displayName.trim() || undefined,
    contactEmail: contactEmail.trim() || undefined,
    contactName: contactName.trim() || undefined,
  });

  const handleValidate = async () => {
    setError(null);
    setValidating(true);
    try {
      const result = await brokerageIntegrationProviderApi.validate(brokerageId, 'MICROSOFT', request());
      setValidation(result);
      if (!result.valid) {
        setError(result.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Validation failed');
      setValidation(null);
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const saved = await brokerageIntegrationProviderApi.configure(brokerageId, 'MICROSOFT', request());
      onUpdated(saved);
      // Clear the secret field — never keep it in component state across an idle period.
      setClientSecret('');
      setValidation(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setError(null);
    setDeleting(true);
    try {
      await brokerageIntegrationProviderApi.delete(brokerageId, 'MICROSOFT');
      onDeleted();
      setConfirmDelete(false);
      setClientId('');
      setClientSecret('');
      setTenantId('');
      setValidation(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleCapabilityToggle = async (key: keyof Pick<BrokerageIntegrationProvider,
    'ssoEnabled' | 'calendarReadEnabled' | 'calendarWriteEnabled' | 'emailReadEnabled' | 'emailSendEnabled'>,
    nextValue: boolean) => {
    setError(null);
    try {
      const updated = await brokerageIntegrationProviderApi.updateCapabilities(brokerageId, 'MICROSOFT', {
        [key]: nextValue,
      });
      onUpdated(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Capability update failed');
    }
  };

  const lastValidatedBadge = () => {
    if (!existing) return null;
    if (existing.lastValidationResult === 'NEVER_VALIDATED') {
      return <Label color="grey">Never validated</Label>;
    }
    if (existing.lastValidationResult === 'VALID') {
      return (
        <Popup
          trigger={<Label color="green"><Icon name="check" /> Valid</Label>}
          content={existing.lastValidatedAt ? `Last validated ${existing.lastValidatedAt}` : 'Last validated recently'}
        />
      );
    }
    return (
      <Popup
        trigger={<Label color="red"><Icon name="exclamation" /> {existing.lastValidationResult}</Label>}
        content={existing.lastValidationMessage ?? 'Last validation failed; see logs'}
      />
    );
  };

  return (
    <Segment>
      <Header as="h2">
        <Icon name="microsoft" />
        <Header.Content>
          Microsoft
          <Header.Subheader>
            Per-brokerage Microsoft Entra app credentials
            {existing && <span style={{ marginLeft: 8 }}>{lastValidatedBadge()}</span>}
          </Header.Subheader>
        </Header.Content>
      </Header>

      {brokerage && <RedirectUriHelper brokerage={brokerage} provider="microsoft" />}

      {error && (
        <Message negative>
          <Message.Header>Something went wrong</Message.Header>
          <p>{error}</p>
        </Message>
      )}

      {validation?.valid && (
        <Message positive>
          <Message.Header>Validation succeeded</Message.Header>
          <p>{validation.message}</p>
          {validation.grantedScopes.length > 0 && (
            <>
              <p style={{ marginBottom: 4 }}>Granted scopes:</p>
              <List bulleted>
                {validation.grantedScopes.map((scope) => (
                  <List.Item key={scope}>{scope}</List.Item>
                ))}
              </List>
            </>
          )}
        </Message>
      )}

      <Form>
        <Form.Input
          id="microsoft-client-id"
          label={<label htmlFor="microsoft-client-id">Client ID</label>}
          placeholder="Application (client) ID from the brokerage's Entra app"
          value={clientId}
          onChange={(_, { value }) => {
            setClientId(value);
            setValidation(null);
          }}
        />
        <Form.Field>
          <label htmlFor="microsoft-client-secret">Client Secret</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              id="microsoft-client-secret"
              type={showSecret ? 'text' : 'password'}
              placeholder={existing?.clientSecretConfigured ? '••••• (configured; paste new to rotate)' : 'New client secret value'}
              value={clientSecret}
              onChange={(e) => {
                setClientSecret(e.target.value);
                setValidation(null);
              }}
              style={{ flex: 1 }}
            />
            <Button type="button" onClick={() => setShowSecret((s) => !s)} icon>
              <Icon name={showSecret ? 'eye slash' : 'eye'} />
            </Button>
          </div>
          <small style={{ color: '#888' }}>
            Encrypted at rest with envelope encryption. Never logged. Never returned by the API once saved.
          </small>
        </Form.Field>
        <Form.Input
          id="microsoft-tenant-id"
          label={<label htmlFor="microsoft-tenant-id">Tenant ID</label>}
          placeholder="Directory (tenant) ID from the brokerage's Entra portal"
          value={tenantId}
          onChange={(_, { value }) => {
            setTenantId(value);
            setValidation(null);
          }}
        />
        <Form.Input
          id="microsoft-display-name"
          label={<label htmlFor="microsoft-display-name">Display name (optional)</label>}
          placeholder='e.g. "Acme Microsoft"'
          value={displayName}
          onChange={(_, { value }) => setDisplayName(value)}
        />
        <Form.Group widths="equal">
          <Form.Input
            id="microsoft-contact-email"
            label={<label htmlFor="microsoft-contact-email">Brokerage IT contact email</label>}
            type="email"
            placeholder="For revalidation-failure notifications"
            value={contactEmail}
            onChange={(_, { value }) => setContactEmail(value)}
          />
          <Form.Input
            id="microsoft-contact-name"
            label={<label htmlFor="microsoft-contact-name">Brokerage IT contact name</label>}
            value={contactName}
            onChange={(_, { value }) => setContactName(value)}
          />
        </Form.Group>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button primary disabled={!canValidate || validating} loading={validating} onClick={handleValidate}>
            <Icon name="check circle outline" />
            Validate
          </Button>
          <Button color="green" disabled={!canSave || saving} loading={saving} onClick={handleSave}>
            <Icon name="save" />
            Save
          </Button>
          {existing && (
            <Button color="red" basic onClick={() => setConfirmDelete(true)}>
              <Icon name="trash" />
              Delete configuration
            </Button>
          )}
        </div>
      </Form>

      <Divider />

      <Header as="h3">Capabilities</Header>
      <p style={{ color: '#666', marginBottom: 16 }}>
        Toggle on the capabilities the brokerage has authorised in Entra.
        Enabling a capability requests the matching Microsoft Graph scope from
        Entra on the next advisor SSO sign-in. Surfaces that don't have an
        advisor-UI yet (Calendar Write, Email surfaces) still capture the
        scope so a later phase can use it without a re-consent.
      </p>

      <CapabilityToggleRow
        label="Single sign-on"
        description="Adds 'Sign in with Microsoft' to the brokerage's advisor login page."
        enabled={existing?.ssoEnabled ?? false}
        disabled={!existing || existing.lastValidationResult !== 'VALID'}
        disabledTooltip={!existing ? 'Save the credentials first' : 'Validate the credentials first'}
        onToggle={(v) => handleCapabilityToggle('ssoEnabled', v)}
      />
      <CapabilityToggleRow
        label="Calendar — Read"
        description="Advisors' Microsoft calendar events become visible on their dashboard and client profiles."
        enabled={existing?.calendarReadEnabled ?? false}
        disabled={!existing || existing.lastValidationResult !== 'VALID'}
        disabledTooltip={!existing ? 'Save the credentials first' : 'Validate the credentials first'}
        onToggle={(v) => handleCapabilityToggle('calendarReadEnabled', v)}
      />
      <CapabilityToggleRow
        label="Calendar — Write"
        description="Advisors can schedule meetings (with Teams links) from inside client profiles."
        enabled={existing?.calendarWriteEnabled ?? false}
        disabled={!existing || existing.lastValidationResult !== 'VALID'}
        disabledTooltip={!existing ? 'Save the credentials first' : 'Validate the credentials first'}
        onToggle={(v) => handleCapabilityToggle('calendarWriteEnabled', v)}
      />
      <CapabilityToggleRow
        label="Email — Read"
        description="Recent email subjects to/from a client surface on the client profile."
        enabled={existing?.emailReadEnabled ?? false}
        disabled={!existing || existing.lastValidationResult !== 'VALID'}
        disabledTooltip={!existing ? 'Save the credentials first' : 'Validate the credentials first'}
        onToggle={(v) => handleCapabilityToggle('emailReadEnabled', v)}
      />
      <CapabilityToggleRow
        label="Email — Send"
        description="Advisors send emails to clients on behalf of their own mailbox."
        enabled={existing?.emailSendEnabled ?? false}
        disabled={!existing || existing.lastValidationResult !== 'VALID'}
        disabledTooltip={!existing ? 'Save the credentials first' : 'Validate the credentials first'}
        onToggle={(v) => handleCapabilityToggle('emailSendEnabled', v)}
      />

      <Modal open={confirmDelete} size="small" onClose={() => setConfirmDelete(false)}>
        <Modal.Header>Delete Microsoft integration?</Modal.Header>
        <Modal.Content>
          <p>This will:</p>
          <List bulleted>
            <List.Item>Remove the credentials from the LOA Platform</List.Item>
            <List.Item>
              Disable the Microsoft Identity Provider on this brokerage's advisor realm
              (Phase 1.4) — existing federated advisor accounts persist
            </List.Item>
            <List.Item>(Phase 2+) revoke all advisor calendar/email connections for this brokerage</List.Item>
          </List>
          <p>
            <b>Confirm only if brokerage IT has rotated the secret or you intend to remove the
            integration entirely.</b>
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="red" loading={deleting} onClick={handleDelete}>
            Delete configuration
          </Button>
        </Modal.Actions>
      </Modal>
    </Segment>
  );
}

export default MicrosoftProviderCard;
